import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	container: {
		display: 'flex',
		flexWrap: 'wrap',
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
});

import { Tabs, Tab, Paper } from "@material-ui/core";

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<div p={3}>
					{children}
				</div>
			)}
		</div>
	);
}

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		configs: {
			isTicketsEnabled: true,
			isQuickAnswersEnabled: true,
			isChatbotEnabled: true,
			isContactsEnabled: true,
			isUsersEnabled: true,
			isQueuesEnabled: true,
			isSchedulesEnabled: true,
			isConnectionsEnabled: true,
			isSettingsEnabled: true,
			isTagsEnabled: true, // Extras based on screenshot assumption
			isCampaignsEnabled: true,
		}
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappIds, setWhatsappIds] = useState([]);
	const [tab, setTab] = useState(0);
	const { loading, whatsApps } = useWhatsApps();

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return {
						...prevState,
						...data,
						configs: data.configs ? data.configs : prevState.configs // Use defaults if DB is null
					};
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappIds(data.whatsapps?.map(w => w.id) || []);
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	const handleSaveUser = async values => {
		const userData = { ...values, whatsappIds, queueIds: selectedQueueIds };
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	const moduleOptions = [
		{ key: "isTicketsEnabled", label: "Conversas" },
		{ key: "isQuickAnswersEnabled", label: "Respostas Rápidas" },
		{ key: "isChatbotEnabled", label: "Chatbot" },
		{ key: "isContactsEnabled", label: "Contatos" },
		{ key: "isUsersEnabled", label: "Atendentes" },
		{ key: "isQueuesEnabled", label: "Setores" },
		{ key: "isSchedulesEnabled", label: i18n.t("schedules.title") }, // "Escalas"
		{ key: "isConnectionsEnabled", label: "Conexões" },
		{ key: "isSettingsEnabled", label: "Ajustes/Configurações" },
		// Extras
		{ key: "isTagsEnabled", label: "Etiquetas" },
		{ key: "isCampaignsEnabled", label: "Campanhas" },
	];

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md" // Increased width for better tab layout
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<Paper square>
								<Tabs
									value={tab}
									indicatorColor="primary"
									textColor="primary"
									onChange={handleTabChange}
									aria-label="device tabs"
								>
									<Tab label="Usuário" />
									{loggedInUser.profile === "admin" && <Tab label="Módulos" />}
									{loggedInUser.profile === "admin" && <Tab label="VoIP" disabled />}
								</Tabs>
							</Paper>

							<DialogContent dividers>
								<TabPanel value={tab} index={0}>
									<div className={classes.multFieldLine}>
										<Field
											as={TextField}
											label={i18n.t("userModal.form.name")}
											autoFocus
											name="name"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextField}
											name="password"
											variant="outlined"
											margin="dense"
											label={i18n.t("userModal.form.password")}
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											type={showPassword ? 'text' : 'password'}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={() => setShowPassword((e) => !e)}
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}}
											fullWidth
										/>
									</div>
									<div className={classes.multFieldLine}>
										<Field
											as={TextField}
											label={i18n.t("userModal.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<FormControl
											variant="outlined"
											className={classes.formControl}
											margin="dense"
										>
											<Can
												role={loggedInUser.profile}
												perform="user-modal:editProfile"
												yes={() => (
													<>
														<InputLabel id="profile-selection-input-label">
															{i18n.t("userModal.form.profile")}
														</InputLabel>

														<Field
															as={Select}
															label={i18n.t("userModal.form.profile")}
															name="profile"
															labelId="profile-selection-label"
															id="profile-selection"
															required
														>
															<MenuItem value="admin">Admin</MenuItem>
															<MenuItem value="user">User</MenuItem>
														</Field>
													</>
												)}
											/>
										</FormControl>
									</div>
									{/* Filas — filtradas pelas conexões selecionadas */}
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (
											<FormControl variant="outlined" margin="dense" fullWidth>
												<InputLabel id="whatsapps-select-label">
													{i18n.t("userModal.form.whatsapp")}
												</InputLabel>
												<Select
													labelId="whatsapps-select-label"
													id="whatsapps-select"
													multiple
													value={whatsappIds}
													onChange={e => setWhatsappIds(e.target.value)}
													label={i18n.t("userModal.form.whatsapp")}
													renderValue={selected =>
														whatsApps
															.filter(w => selected.includes(w.id))
															.map(w => w.name)
															.join(", ")
													}
												>
													{whatsApps.map(whatsapp => (
														<MenuItem key={whatsapp.id} value={whatsapp.id}>
															{whatsapp.name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										)}
									/>

									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => {
											// Filtra as filas disponíveis com base nos whatsapps selecionados
											const availableQueues = [];
											whatsApps
												.filter(w => whatsappIds.includes(w.id))
												.forEach(w => {
													(w.queues || []).forEach(q => {
														if (!availableQueues.find(aq => aq.id === q.id)) {
															availableQueues.push(q);
														}
													});
												});

											return (
												<FormControl variant="outlined" margin="dense" fullWidth style={{ marginTop: 8 }}>
													<InputLabel id="queues-select-label">
														{i18n.t("userModal.form.queues")}
													</InputLabel>
													<Select
														labelId="queues-select-label"
														id="queues-select"
														multiple
														value={selectedQueueIds}
														onChange={e => setSelectedQueueIds(e.target.value)}
														label={i18n.t("userModal.form.queues")}
														renderValue={selected =>
															availableQueues
																.filter(q => selected.includes(q.id))
																.map(q => q.name)
																.join(", ")
														}
														disabled={availableQueues.length === 0}
													>
														{availableQueues.map(queue => (
															<MenuItem key={queue.id} value={queue.id}>
																{queue.name}
															</MenuItem>
														))
														}
													</Select>
												</FormControl>
											);
										}}
									/>

								</TabPanel>

								{loggedInUser.profile === "admin" && (
									<TabPanel value={tab} index={1}>
										<div className={classes.container}>
											{moduleOptions.map((module) => (
												<FormControl key={module.key} variant="outlined" margin="dense" style={{ width: '100%', marginBottom: 10 }}>
													<InputLabel>{module.label}</InputLabel>
													<Select
														label={module.label}
														value={values.configs?.[module.key] === undefined ? true : values.configs[module.key]}
														onChange={(e) => setFieldValue(`configs.${module.key}`, e.target.value)}
													>
														<MenuItem value={true}>Sim</MenuItem>
														<MenuItem value={false}>Não</MenuItem>
													</Select>
												</FormControl>
											))}
										</div>
									</TabPanel>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
